import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFields } from '../api';
import { mapRef } from '../map/mapRef';
import { useStore } from '../store';
import { Panel } from './Panel';
import { IconBack, IconTrash } from './icons';

function Stepper({
  label,
  value,
  onDec,
  onInc,
}: {
  label: string;
  value: string;
  onDec: () => void;
  onInc: () => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-between">
      <span className="text-sm text-white/70">{label}</span>
      <div className="flex items-center gap-3">
        <button
          onClick={onDec}
          className="h-11 w-11 rounded-xl bg-zinc-700 text-xl active:scale-95"
        >
          −
        </button>
        <span className="w-24 text-center text-xl font-semibold tabular-nums">{value}</span>
        <button
          onClick={onInc}
          className="h-11 w-11 rounded-xl bg-zinc-700 text-xl active:scale-95"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function FieldPanel() {
  const { t } = useTranslation();
  const phase = useStore((s) => s.phase);
  const fieldsSource = useStore((s) => s.fieldsSource);
  const selectedField = useStore((s) => s.selectedField);
  const widthM = useStore((s) => s.widthM);
  const headingDeg = useStore((s) => s.headingDeg);
  const headlandRounds = useStore((s) => s.headlandRounds);
  const courseMode = useStore((s) => s.courseMode);
  const stats = useStore((s) => s.stats);
  const savedCourses = useStore((s) => s.savedCourses);
  const setWidth = useStore((s) => s.setWidth);
  const setHeadlandRounds = useStore((s) => s.setHeadlandRounds);
  const setCourseMode = useStore((s) => s.setCourseMode);
  const optimize = useStore((s) => s.optimize);
  const setFields = useStore((s) => s.setFields);
  const generateCourse = useStore((s) => s.generateCourse);
  const backToBrowse = useStore((s) => s.backToBrowse);
  const loadCourse = useStore((s) => s.loadCourse);
  const deleteSavedCourse = useStore((s) => s.deleteSavedCourse);
  const startDrawField = useStore((s) => s.startDrawField);
  const startEditField = useStore((s) => s.startEditField);
  const startAddZone = useStore((s) => s.startAddZone);
  const pendingResume = useStore((s) => s.pendingResume);
  const acceptResume = useStore((s) => s.acceptResume);
  const discardResume = useStore((s) => s.discardResume);
  const savedImplements = useStore((s) => s.implements);
  const applyImplement = useStore((s) => s.applyImplement);
  const saveCurrentImplement = useStore((s) => s.saveCurrentImplement);
  const deleteSavedImplement = useStore((s) => s.deleteSavedImplement);
  const [loading, setLoading] = useState(false);
  const [implName, setImplName] = useState('');
  const [savingImpl, setSavingImpl] = useState(false);

  async function loadFields() {
    const ctrl = mapRef.current;
    if (!ctrl) return;
    setLoading(true);
    try {
      const fc = await fetchFields(ctrl.bbox());
      setFields(fc);
      if (fc.features.length > 0) ctrl.fitTo(fc);
    } catch {
      /* surfaced via empty list */
    } finally {
      setLoading(false);
    }
  }

  if (phase === 'idle') {
    return (
      <Panel>
        <h2 className="text-lg font-semibold">{t('idle.title')}</h2>
        <p className="mt-1 text-sm text-white/60">{t('idle.hint')}</p>
        <button
          onClick={loadFields}
          disabled={loading}
          className="mt-3 w-full rounded-2xl bg-emerald-600 py-2.5 text-base font-semibold active:scale-[0.99] disabled:opacity-50 md:mt-4 md:py-3"
        >
          {loading ? t('idle.loading') : t('idle.loadHere')}
        </button>
        {fieldsSource && (
          <p className="mt-2 text-center text-xs text-white/50">
            {t(`idle.source.${fieldsSource}`)}
          </p>
        )}

        <button
          onClick={startDrawField}
          className="mt-2 w-full rounded-2xl border border-white/20 py-2.5 text-sm font-medium text-white/80 active:scale-[0.99]"
        >
          {t('editor.drawField')}
        </button>

        {savedCourses.length > 0 && (
          <div className="mt-4 md:mt-5">
            <h3 className="text-sm font-semibold text-white/70">{t('idle.saved')}</h3>
            <ul className="mt-2 space-y-2">
              {savedCourses.map((c) => (
                <li key={c.id} className="flex items-center gap-2 rounded-xl bg-zinc-800 p-2">
                  <button
                    onClick={() => loadCourse(c)}
                    className="flex-1 truncate text-left text-sm font-medium"
                    title={c.name}
                  >
                    {c.name}
                    <span className="ml-2 text-xs text-white/40">{c.widthM.toFixed(1)} m</span>
                  </button>
                  <button
                    onClick={() => void deleteSavedCourse(c.id)}
                    aria-label={t('common.delete')}
                    className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-700 text-white/70"
                  >
                    <IconTrash className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>
    );
  }

  if (phase === 'field' && selectedField) {
    const name = (selectedField.properties.name as string) ?? t('field.title');
    return (
      <Panel>
        <h2 className="text-lg font-semibold">{name}</h2>

        {pendingResume && (
          <div className="mt-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3">
            <div className="text-sm font-semibold text-amber-200">{t('session.resumeTitle')}</div>
            <p className="mt-1 text-xs text-white/60">{t('session.resumeHint')}</p>
            <div className="mt-2 flex gap-2">
              <button
                onClick={acceptResume}
                className="flex-1 rounded-xl bg-amber-500 py-2 text-sm font-semibold text-black active:scale-[0.99]"
              >
                {t('session.resume')}
              </button>
              <button
                onClick={discardResume}
                className="rounded-xl bg-zinc-700 px-3 py-2 text-sm font-medium text-white/80 active:scale-95"
              >
                {t('session.startFresh')}
              </button>
            </div>
          </div>
        )}

        <Stepper
          label={t('field.width')}
          value={`${widthM.toFixed(1)} m`}
          onDec={() => setWidth(widthM - 0.5)}
          onInc={() => setWidth(widthM + 0.5)}
        />

        {/* Saved implements: tap to apply its width; save the current width as a new one. */}
        <div className="mt-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {savedImplements.map((impl) => (
              <span
                key={impl.id}
                className="flex items-center gap-1 rounded-full bg-zinc-700/70 py-1 pl-3 pr-1 text-xs"
              >
                <button onClick={() => applyImplement(impl.id)} className="font-medium">
                  {impl.name}
                  <span className="ml-1 text-white/40">{impl.widthM.toFixed(1)} m</span>
                </button>
                <button
                  onClick={() => void deleteSavedImplement(impl.id)}
                  aria-label={t('common.delete')}
                  className="flex h-5 w-5 items-center justify-center rounded-full text-white/40 hover:text-white"
                >
                  ×
                </button>
              </span>
            ))}
            {!savingImpl && (
              <button
                onClick={() => setSavingImpl(true)}
                className="rounded-full border border-white/20 px-3 py-1 text-xs text-white/70 active:scale-95"
              >
                + {t('implement.save')}
              </button>
            )}
          </div>
          {savingImpl && (
            <div className="mt-2 flex gap-2">
              <input
                value={implName}
                onChange={(e) => setImplName(e.target.value)}
                placeholder={t('implement.namePlaceholder')}
                className="min-w-0 flex-1 rounded-lg bg-zinc-800 px-3 py-1.5 text-sm outline-none"
              />
              <button
                onClick={async () => {
                  await saveCurrentImplement(implName);
                  setImplName('');
                  setSavingImpl(false);
                }}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold active:scale-95"
              >
                {t('course.save')}
              </button>
            </div>
          )}
        </div>

        {/* Course shape: straight parallel passes vs. boundary-following rings. */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-white/70">{t('field.mode')}</span>
          <div className="flex overflow-hidden rounded-xl bg-zinc-700/60 text-sm font-medium">
            <button
              onClick={() => setCourseMode('parallel')}
              className={`px-3 py-1.5 ${courseMode === 'parallel' ? 'bg-emerald-600' : 'text-white/70'}`}
            >
              {t('field.modeParallel')}
            </button>
            <button
              onClick={() => setCourseMode('contour')}
              className={`px-3 py-1.5 ${courseMode === 'contour' ? 'bg-emerald-600' : 'text-white/70'}`}
            >
              {t('field.modeContour')}
            </button>
          </div>
        </div>

        {courseMode === 'parallel' && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-white/70">{t('field.heading')}</span>
            <div className="flex items-center gap-2">
              <span className="text-xl font-semibold tabular-nums">
                {Math.round(headingDeg ?? 0)}°
              </span>
              <button
                onClick={optimize}
                className="rounded-lg bg-zinc-700 px-2.5 py-1 text-xs font-medium text-white/80 active:scale-95"
              >
                {t('field.optimize')}
              </button>
            </div>
          </div>
        )}
        {courseMode === 'parallel' && (
          <p className="mt-1 text-right text-xs text-white/40">↻ {t('field.headingHint')}</p>
        )}

        <Stepper
          label={t('field.headland')}
          value={t('field.rounds', { n: headlandRounds })}
          onDec={() => setHeadlandRounds(headlandRounds - 1)}
          onInc={() => setHeadlandRounds(headlandRounds + 1)}
        />

        {stats && (
          <p className="mt-3 rounded-xl bg-zinc-800/70 px-3 py-2 text-center text-sm tabular-nums text-white/80">
            {t('field.stats', {
              lanes: stats.lanes,
              turns: stats.turns,
              km: (stats.totalLengthM / 1000).toFixed(1),
              min: Math.round(stats.estMinutes),
            })}
          </p>
        )}

        <div className="mt-3 flex gap-2 md:mt-4">
          <button
            onClick={startEditField}
            className="flex-1 rounded-xl bg-zinc-700 py-2.5 text-sm font-medium active:scale-[0.99]"
          >
            {t('editor.editField')}
          </button>
          <button
            onClick={startAddZone}
            className="flex-1 rounded-xl bg-zinc-700 py-2.5 text-sm font-medium active:scale-[0.99]"
          >
            {t('editor.zones')}
          </button>
        </div>

        <div className="mt-3 flex gap-3 md:mt-4">
          <button
            onClick={backToBrowse}
            aria-label={t('field.back')}
            className="flex items-center justify-center rounded-2xl bg-zinc-700 px-5 py-3"
          >
            <IconBack />
          </button>
          <button
            onClick={generateCourse}
            className="flex-1 rounded-2xl bg-emerald-600 py-3 font-semibold active:scale-[0.99]"
          >
            {t('field.generate')}
          </button>
        </div>
      </Panel>
    );
  }

  return null;
}
